import { type FormEvent, useState } from "react";
import { Link } from "react-router";
import { APP_NAME, ApiError, authApi } from "../../lib/api";
import { useAppPreferences } from "../../lib/preferences";

export function ForgotPasswordPage() {
  const { t } = useAppPreferences();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    setSent(false);

    try {
      await authApi.forgotPassword(email.trim());
      setSent(true);
    } catch (requestError) {
      setError(
        requestError instanceof ApiError
          ? requestError.detail
          : t("Unable to request a reset link. Please try again."),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="shell-grid min-h-screen px-6 py-10 text-slate-100">
      <div className="mx-auto grid w-full max-w-3xl gap-6">
        <form
          className="glass-panel rounded-[32px] border border-white/10 p-8 sm:p-10"
          onSubmit={handleSubmit}
        >
          <p className="text-xs font-semibold uppercase tracking-[0.32em] text-sky-200">
            {t("{appName} account recovery", { appName: APP_NAME })}
          </p>
          <div className="mt-5 space-y-2">
            <h1 className="text-4xl font-semibold tracking-tight text-white">
              {t("Reset your password")}
            </h1>
            <p className="text-sm leading-7 text-slate-300">
              {t("Enter the email on your gaamed account. If it exists, we will send a reset link.")}
            </p>
          </div>

          <label className="mt-8 block">
            <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">
              {t("Email")}
            </span>
            <input
              autoComplete="email"
              className="w-full rounded-[20px] border border-white/10 bg-slate-950/50 px-4 py-3 text-base text-white outline-none transition placeholder:text-slate-500 focus:border-sky-300/70 focus:bg-slate-950/70"
              onChange={(event) => setEmail(event.target.value)}
              placeholder="provider@gaamed.com"
              required
              type="email"
              value={email}
            />
          </label>

          {sent ? (
            <div className="mt-4 rounded-[20px] border border-emerald-400/20 bg-emerald-400/10 px-4 py-3 text-sm leading-7 text-emerald-100">
              {t("If an account exists for that email, a reset link is on its way.")}
            </div>
          ) : null}

          {error ? (
            <div
              className="mt-4 flex items-start justify-between gap-3 rounded-[20px] border border-rose-400/20 bg-rose-400/10 px-4 py-3 text-sm text-rose-100"
              role="alert"
            >
              <span>{error}</span>
              <button
                className="shrink-0 rounded-full border border-rose-200/20 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-rose-100 transition hover:bg-rose-200/10"
                onClick={() => setError(null)}
                type="button"
              >
                {t("Dismiss")}
              </button>
            </div>
          ) : null}

          <button
            className="mt-6 w-full rounded-full bg-sky-300 px-5 py-3 text-sm font-semibold uppercase tracking-[0.24em] text-slate-950 transition hover:bg-sky-200 disabled:cursor-not-allowed disabled:opacity-70"
            disabled={submitting}
            type="submit"
          >
            {submitting ? t("Sending...") : t("Send reset link")}
          </button>

          <p className="mt-6 text-xs leading-6 text-slate-400">
            {t("Remember your password?")} {" "}
            <Link className="ops-action-link font-semibold text-sky-200" to="/sign-in">
              {t("Back to sign-in")}
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
